/* START_MODULE_CONTRACT
 * PURPOSE: Дерево навигации по проектам с поддержкой свёрнутого бокового меню.
 * SCOPE: Список проектов, разделы проекта, popover в mini-режиме sidebar.
 * DEPENDS: M-FE-PROJECTS
 * LINKS: M-FE-PROJECTS, M-FE-LAYOUT
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * ProjectsNav - навигация по проектам и их разделам.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-13 - Стили навигации под тёмный sidebar (брендовая палитра).
 * END_CHANGE_SUMMARY
 */

import { useEffect, useState } from 'react';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import FolderIcon from '@mui/icons-material/Folder';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ProjectAvatar from '../../components/ProjectAvatar';
import { useProjects } from '../../context/ProjectsContext';
import { sidebarNavItemSx } from '../../theme/appTheme';

const PROJECT_SECTIONS = [
  { key: 'roles', label: 'Проектные роли', icon: <GroupWorkIcon fontSize="small" /> },
  { key: 'employees', label: 'Сотрудники', icon: <PeopleIcon fontSize="small" /> },
  { key: 'features', label: 'Задачи', icon: <AssignmentIcon fontSize="small" /> },
];

const miniItemSx = {
  justifyContent: 'center',
  px: 1.5,
  minHeight: 48,
  borderRadius: 0.5,
  mx: 1,
};

const miniIconSx = {
  minWidth: 0,
  justifyContent: 'center',
};

/* START_METHOD_ProjectsNav */
/* START_CONTRACT: ProjectsNav
 * PURPOSE: Дерево навигации по проектам и их разделам с раскрытием активного проекта; в collapsed — popover.
 * INPUTS: { onNavigate?: () => void, collapsed?: boolean }.
 * OUTPUTS: { JSX.Element }.
 * SIDE_EFFECTS: Навигация (useNavigate); локальное состояние раскрытия.
 * LINKS: M-FE-PROJECTS
 * END_CONTRACT: ProjectsNav
 */
export default function ProjectsNav({ onNavigate, collapsed = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { projects, loading, error } = useProjects();

  const match = matchPath('/projects/:projectId/*', location.pathname);
  const activeProjectId = match ? Number(match.params.projectId) : null;
  const activeSection = match ? (match.params['*'] || '').split('/')[0] : null;
  const onProjectsRoot = location.pathname === '/projects';

  const [open, setOpen] = useState(true);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const popoverOpen = Boolean(popoverAnchor);

  useEffect(() => {
    if (!activeProjectId) return;
    setOpen(true);
    setExpandedIds((prev) => {
      if (prev.has(activeProjectId)) return prev;
      const next = new Set(prev);
      next.add(activeProjectId);
      return next;
    });
  }, [activeProjectId]);

  const go = (path) => {
    navigate(path);
    onNavigate?.();
    setPopoverAnchor(null);
  };

  const toggleProjectsOpen = (event) => {
    event.stopPropagation();
    setOpen((prev) => !prev);
  };

  const toggleProjectExpanded = (event, id) => {
    event.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleProjectsClick = () => {
    setOpen(true);
    go('/projects');
  };

  const handleProjectClick = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    go(`/projects/${id}/timeline`);
  };

  const handlePopoverOpen = (event) => {
    setPopoverAnchor(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
  };

  const renderProjectsTree = (inPopover = false) => {
    const listSx = inPopover ? { width: 280, py: 0.5 } : undefined;
    const itemSx = (selected, extra = {}) => (inPopover ? { py: 1, ...extra } : { ...sidebarNavItemSx(selected), ...extra });

    return (
      <>
        {!inPopover && (
          <ListItemButton
            selected={onProjectsRoot}
            onClick={handleProjectsClick}
            sx={{
              ...itemSx(onProjectsRoot),
              ...(collapsed ? miniItemSx : {}),
            }}
          >
            <ListItemIcon sx={collapsed ? miniIconSx : { minWidth: 40 }}>
              <FolderIcon fontSize="small" />
            </ListItemIcon>
            {!collapsed && (
              <>
                <ListItemText primary="Проекты" primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
                <IconButton
                  size="small"
                  edge="end"
                  aria-label={open ? 'Свернуть список проектов' : 'Развернуть список проектов'}
                  onClick={toggleProjectsOpen}
                  sx={{ color: 'text.secondary' }}
                >
                  {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
              </>
            )}
          </ListItemButton>
        )}

        {inPopover && (
          <ListItemButton selected={onProjectsRoot} onClick={handleProjectsClick} sx={{ py: 1 }}>
            <ListItemIcon><FolderIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Все проекты" primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
          </ListItemButton>
        )}

        <Collapse in={inPopover || open} timeout="auto" unmountOnExit>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
              <CircularProgress size={20} />
            </Box>
          ) : error ? (
            <Typography variant="caption" color="error" sx={{ display: 'block', px: inPopover ? 2 : 4, py: 1 }}>
              {error}
            </Typography>
          ) : projects.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: inPopover ? 2 : 4, py: 1 }}>
              Нет проектов
            </Typography>
          ) : (
            <List component="div" disablePadding sx={listSx}>
              {projects.map((project) => {
                const isExpanded = expandedIds.has(project.id);
                const isActiveProject = activeProjectId === project.id;
                const timelineSelected = isActiveProject && activeSection === 'timeline';
                return (
                  <Box key={project.id}>
                    <ListItemButton
                      sx={itemSx(timelineSelected, { pl: inPopover ? 2 : 3 })}
                      selected={timelineSelected}
                      onClick={() => handleProjectClick(project.id)}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <ProjectAvatar name={project.name} size={24} />
                      </ListItemIcon>
                      <ListItemText
                        primary={project.name}
                        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                      />
                      <IconButton
                        size="small"
                        edge="end"
                        aria-label={isExpanded ? 'Свернуть проект' : 'Развернуть проект'}
                        onClick={(event) => toggleProjectExpanded(event, project.id)}
                        sx={{ color: 'text.secondary' }}
                      >
                        {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    </ListItemButton>

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {PROJECT_SECTIONS.map((section) => {
                          const sectionSelected = isActiveProject && activeSection === section.key;
                          return (
                            <ListItemButton
                              key={section.key}
                              sx={itemSx(sectionSelected, { pl: inPopover ? 4 : 6 })}
                              selected={sectionSelected}
                              onClick={() => go(`/projects/${project.id}/${section.key}`)}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>{section.icon}</ListItemIcon>
                              <ListItemText
                                primary={section.label}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  </Box>
                );
              })}
            </List>
          )}
        </Collapse>
      </>
    );
  };

  if (collapsed) {
    const activeProject = projects.find((p) => p.id === activeProjectId);
    const projectsActive = onProjectsRoot || !!activeProjectId;

    return (
      <>
        <Tooltip title="Проекты" placement="right" arrow>
          <ListItemButton
            selected={projectsActive}
            onClick={handlePopoverOpen}
            sx={{ ...sidebarNavItemSx(projectsActive), ...miniItemSx }}
            aria-label="Проекты"
          >
            <ListItemIcon sx={miniIconSx}>
              {activeProject ? (
                <ProjectAvatar name={activeProject.name} size={28} />
              ) : (
                <FolderIcon />
              )}
            </ListItemIcon>
          </ListItemButton>
        </Tooltip>

        <Popover
          open={popoverOpen}
          anchorEl={popoverAnchor}
          onClose={handlePopoverClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                ml: 0.5,
                borderRadius: 1,
                boxShadow: 4,
                maxHeight: '70vh',
                overflowY: 'auto',
              },
            },
          }}
        >
          <Box sx={{ py: 0.5 }}>
            {renderProjectsTree(true)}
          </Box>
        </Popover>
      </>
    );
  }

  return renderProjectsTree(false);
}
/* END_METHOD_ProjectsNav */
